import type { QueryClient } from "@tanstack/react-query";
import { readPnl } from "./pnl-display";

type AnyRecord = Record<string, any>;

const positionSummary = (positions: AnyRecord[]) => {
  let total = 0;
  let unknown = 0;
  for (const position of positions) {
    if (!["active", "open", "closing"].includes(String(position?.status ?? "").toLowerCase())) continue;
    const pnl =
      position?.unrealized_pnl !== undefined
        ? position.unrealized_pnl
        : position?.unrealizedPnl !== undefined
          ? position.unrealizedPnl
          : position?.pnl;
    const reading = readPnl(pnl);
    if (reading.known) total += reading.value;
    else unknown += 1;
  }
  return { total, unknown };
};

const rebuildPositionSummary = (oldData: AnyRecord, positions: AnyRecord[]) => {
  const { total, unknown } = positionSummary(positions);
  const updateSummary = (summary: unknown) =>
    summary && typeof summary === "object" && !Array.isArray(summary)
      ? {
          ...summary,
          total_unrealized_pnl: total,
          unknown_pnl_positions: unknown,
        }
      : summary;
  const results = oldData.results;
  if (!results || typeof results !== "object" || Array.isArray(results)) {
    return oldData.summary !== undefined
      ? { summary: updateSummary(oldData.summary) }
      : {};
  }
  return {
    ...(oldData.summary !== undefined ? { summary: updateSummary(oldData.summary) } : {}),
    results: {
      ...results,
      ...(Array.isArray(results.positions) ? { positions } : {}),
      ...(results.summary !== undefined ? { summary: updateSummary(results.summary) } : {}),
    },
  };
};

/**
 * F1-A: the ONE positions-cache container walker (extracted from
 * apply-trading-events.ts, the SIG-345-correct copy).
 *
 * SIG-345: v2 list responses carry the SAME rows in BOTH `items` (which the
 * UI's select reads first) and `results.positions` (legacy mirror). A patch
 * must land on every copy present — updating only results.positions returned
 * "handled" while the UI kept rendering the untouched `items`, so the close
 * leg waited out the 1.5s trailing refetch (~2s repaint vs the <1s contract).
 */
export const getPositionListContainer = (oldData: AnyRecord | undefined) => {
  const hasItems = Array.isArray(oldData?.items);
  const hasResultsPositions = Array.isArray(oldData?.results?.positions);
  if (hasItems || hasResultsPositions) {
    return {
      positions: (hasItems
        ? oldData!.items
        : oldData!.results.positions) as AnyRecord[],
      rebuild: (positions: AnyRecord[]) => ({
        ...oldData,
        ...(hasItems ? { items: positions } : {}),
        ...(hasResultsPositions
          ? { results: { ...oldData!.results, positions } }
          : {}),
        ...rebuildPositionSummary(oldData!, positions),
      }),
    };
  }
  if (Array.isArray(oldData?.positions)) {
    return {
      positions: oldData!.positions as AnyRecord[],
      rebuild: (positions: AnyRecord[]) => ({
        ...oldData,
        positions,
        ...rebuildPositionSummary(oldData!, positions),
      }),
    };
  }
  return null;
};

/** Apply `update` to every row of every cached ["positions"] container copy. */
export const rebuildPositionCaches = (
  queryClient: QueryClient,
  update: (position: AnyRecord) => AnyRecord,
): void => {
  queryClient.setQueriesData(
    { queryKey: ["positions"] },
    (oldData: AnyRecord | undefined) => {
      const container = getPositionListContainer(oldData);
      if (!container) return oldData;
      return container.rebuild(container.positions.map(update));
    },
  );
};

const OPENISH_STATUS = new Set(["active", "open", "closing"]);

function positionAccountKeys(position: AnyRecord): string[] {
  return [
    position.account,
    position.account_id,
    position.broker_account_id,
    position.brokerAccountId,
  ]
    .filter((value) => value != null && value !== "")
    .map(String);
}

/**
 * Drop open/closing rows whose account key is in `accountKeys` from every
 * RAW cache copy (`items` first AND `results.positions`). Closed rows stay.
 * Identity-stable when the set is empty or no row matches.
 */
export const dropOpenPositionsForAccounts = (
  queryClient: QueryClient,
  accountKeys: ReadonlySet<string>,
): void => {
  if (accountKeys.size === 0) return;
  queryClient.setQueriesData(
    { queryKey: ["positions"] },
    (oldData: AnyRecord | undefined) => {
      const container = getPositionListContainer(oldData);
      if (!container) return oldData;
      let dropped = false;
      const next = container.positions.filter((position) => {
        const status = String(position?.status ?? "").toLowerCase();
        if (!OPENISH_STATUS.has(status)) return true;
        if (!positionAccountKeys(position).some((key) => accountKeys.has(key))) {
          return true;
        }
        dropped = true;
        return false;
      });
      if (!dropped) return oldData;
      return container.rebuild(next);
    },
  );
};
